import * as AgGrid from "ag-grid-community";
import * as columns from "grid/columns";
import * as datasource from "grid/datasource";
import * as events from "utils/events";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import * as notifications from "utils/notifications";
import { ACTIONS_COL_DEF } from "grid/columns";
import { APP_EVENTS } from "utils/events";
import { GridController } from "grid/grid_controller";
import { onActionCellClicked } from "grid/row_actions";

export enum Action {
  BLOCK = "block",
}

interface UnblockedAccount {
  account_number: number;
  company_name: string | null;
  block_reason: string;
  block_account_path: string;
}

export default class extends GridController<UnblockedAccount> {
  $searchInput: HTMLInputElement;

  override connect() {
    this.$searchInput = document.querySelector(".search-input");
    super.connect();

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.setupSearchEnterEvent();
    this.setupDataChangeListeners();
  }

  private setupSearchEnterEvent(): void {
    events.subscribeOnEnter(this.$searchInput, this.onSearchEnterPressed);
    document.addEventListener(APP_EVENTS.SEARCH_ENTER_PRESSED, this.onFilterChanged);
  }

  private setupDataChangeListeners(): void {
    document.addEventListener(APP_EVENTS.ACCOUNT_STATUS_CHANGED, this.onAccountStatusChanged);
  }

  private onSearchEnterPressed = (): void => {
    document.dispatchEvent(new CustomEvent(APP_EVENTS.SEARCH_ENTER_PRESSED));
  };

  private onFilterChanged = (): void => {
    if (this.gridApi) {
      this.gridApi.onFilterChanged();
    }
  };

  private onAccountStatusChanged = (): void => {
    if (this.gridApi) {
      this.gridApi.purgeInfiniteCache();
    }
  };

  override buildGrid() {
    const ctrl = this;
    const { indexPath, i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const colDefs: AgGrid.ColDef[] = [
      { field: "account_number", ...columns.DEFAULT_COL_DEF, ...columns.TEXT_FILTER_COL_DEF, sortable: true },
      { field: "company_name", ...columns.DEFAULT_COL_DEF, ...columns.TEXT_FILTER_COL_DEF, sortable: true },
      {
        field: "block_reason",
        ...columns.DEFAULT_COL_DEF,
        editable: true,
        sortable: false,
      },
      {
        ...ACTIONS_COL_DEF,
        onCellClicked(params) {
          onActionCellClicked<Action>(params, async function (action) {
            switch (action) {
              case Action.BLOCK:
                return await ctrl.handleBlockAccount(params.data);
            }
          });
        },
      },
    ];

    const dataSource = new datasource.Datasource({
      callbacks: {
        getSearchInput: () => (this.$searchInput ? this.$searchInput.value : ""),
      },
      indexPath,
      limit: paginationPageSize,
    });

    const options: AgGrid.GridOptions<UnblockedAccount> = {
      ...gridUtils.DEFAULT_GRID_OPTIONS,
      defaultColDef: gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef,
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, {
        i18n: JSON.parse(i18nJson),
      }),
      localeText: {
        ...columns.FILTER_TRANSLATIONS,
      },
      datasource: dataSource,
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, options);
    dataSource.gridApi = gridApi;
    return gridApi;
  }

  async handleBlockAccount(data: UnblockedAccount) {
    const response = await http.httpPost(data.block_account_path, {
      account: {
        account_number: data.account_number,
        block_reason: data.block_reason || "",
      },
    });

    if (response.ok) {
      const responseData = await response.json();
      notifications.success(responseData.success);
      document.dispatchEvent(new CustomEvent(APP_EVENTS.ACCOUNT_STATUS_CHANGED));
    } else {
      const responseData = await response.json();
      notifications.alert(responseData.error);
    }
  }
}
